import { useState, useCallback, useRef } from 'react'
import { headlessGenerate } from 'src/api/headless'
import URL from 'src/api/url'

const MAX_RECONNECT_ATTEMPTS = 3
const RECONNECT_DELAYS_MS = [1000, 2000, 4000]

const useGenerateStream = () => {
  const [generating, setGenerating] = useState(false)
  const [progress, setProgress] = useState(0)
  const [step, setStep] = useState('')
  const [result, setResult] = useState(null)
  const [error, setError] = useState(null)
  const [jobId, setJobId] = useState(null)
  const [reconnecting, setReconnecting] = useState(false)
  const [reconnectAttempt, setReconnectAttempt] = useState(0)
  const eventSourceRef = useRef(null)
  const retryCountRef = useRef(0)
  const retryTimeoutRef = useRef(null)

  const connectToStream = useCallback((id) => {
    const streamUrl = `${URL.APP_URL_LLM}collection-builder/generate/${id}/stream`

    return new Promise((resolve, reject) => {
      let settled = false

      const connect = () => {
        if (eventSourceRef.current) {
          eventSourceRef.current.close()
        }

        const evtSource = new EventSource(streamUrl)
        eventSourceRef.current = evtSource

        evtSource.onmessage = event => {
          // Reset retry count on any successful message
          retryCountRef.current = 0
          setReconnecting(false)
          setReconnectAttempt(0)

          try {
            const eventData = JSON.parse(event.data)
            const pct = eventData.progress || 0
            setProgress(pct)
            setStep(eventData.current_step || '')

            if (eventData.status === 'completed') {
              settled = true
              evtSource.close()
              setResult(eventData.result)
              setGenerating(false)
              resolve(eventData.result)
            } else if (eventData.status === 'failed') {
              settled = true
              evtSource.close()
              const errMsg = eventData.error || 'Unknown error'
              setError(errMsg)
              setGenerating(false)
              reject(new Error(errMsg))
            } else if (eventData.status === 'cancelled') {
              settled = true
              evtSource.close()
              setError('Job was cancelled')
              setGenerating(false)
              reject(new Error('Job was cancelled'))
            }
          } catch (e) {
            // parse error
          }
        }

        evtSource.onerror = () => {
          evtSource.close()
          if (settled) return

          if (retryCountRef.current < MAX_RECONNECT_ATTEMPTS) {
            retryCountRef.current += 1
            setReconnecting(true)
            setReconnectAttempt(retryCountRef.current)

            const delay = RECONNECT_DELAYS_MS[retryCountRef.current - 1]
            retryTimeoutRef.current = setTimeout(() => {
              connect()
            }, delay)
          } else {
            setReconnecting(false)
            setReconnectAttempt(0)
            setError('SSE connection lost')
            setGenerating(false)
            reject(new Error('SSE connection lost'))
          }
        }
      }

      connect()
    })
  }, [])

  const startGenerate = useCallback(async generateData => {
    setGenerating(true)
    setProgress(0)
    setStep('Starting...')
    setResult(null)
    setError(null)
    setJobId(null)
    setReconnecting(false)
    setReconnectAttempt(0)
    retryCountRef.current = 0
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current)
    }

    try {
      const data = await headlessGenerate(generateData)
      const id = data.job_id
      setJobId(id)
      return connectToStream(id)
    } catch (e) {
      setError(e.message || 'Failed to start generate')
      setGenerating(false)
      throw e
    }
  }, [connectToStream])

  const reconnectToJob = useCallback((id) => {
    setGenerating(true)
    setProgress(0)
    setStep('Reconnecting...')
    setResult(null)
    setError(null)
    setJobId(id)
    setReconnecting(false)
    setReconnectAttempt(0)
    retryCountRef.current = 0
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current)
    }
    return connectToStream(id)
  }, [connectToStream])

  const setResultDirectly = useCallback((data) => {
    setGenerating(false)
    setProgress(100)
    setStep('Complete')
    setResult(data)
    setError(null)
  }, [])

  const cancel = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close()
      eventSourceRef.current = null
    }
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current)
    }
    setGenerating(false)
    setReconnecting(false)
  }, [])

  return {
    generating,
    progress,
    step,
    result,
    error,
    jobId,
    reconnecting,
    reconnectAttempt,
    startGenerate,
    reconnectToJob,
    setResultDirectly,
    cancel,
  }
}

export default useGenerateStream
